import { useState } from 'react'
import { Pencil, Trash2, Eye, Search, Image as ImageIcon, Filter } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { Input } from '@/shared/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { cn } from '@/shared/utils'
import type { InventoryItem, FoodItem, ClothingItem, vendorType } from '@/shared/types'

interface InventoryTableProps {
  items: InventoryItem[]
  vendorType: vendorType
  onEdit: (item: InventoryItem) => void
  onDelete: (item: InventoryItem) => void
  onToggleAvailability: (item: FoodItem) => void
  onViewVariants: (item: ClothingItem) => void
  loading: boolean
}

type FilterStatus = 'all' | 'available' | 'unavailable'

export function InventoryTable({
  items,
  vendorType,
  onEdit,
  onDelete,
  onToggleAvailability,
  onViewVariants,
  loading,
}: InventoryTableProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')

  const isFoodVendor = vendorType === 'FOOD'

  // Calculate counts for each filter
  const getItemAvailability = (item: InventoryItem) => {
    if (isFoodVendor) {
      return (item as FoodItem).isAvailable
    }
    return (item as ClothingItem).variants.some((v) => v.stockQty > 0)
  }

  const availableCount = items.filter(getItemAvailability).length
  const unavailableCount = items.length - availableCount

  // Filter and search
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (isFoodVendor
        ? (item as FoodItem).description?.toLowerCase().includes(searchQuery.toLowerCase())
        : (item as ClothingItem).brand?.toLowerCase().includes(searchQuery.toLowerCase()))

    if (!matchesSearch) return false
    if (filterStatus === 'all') return true

    const isAvailable = getItemAvailability(item)
    return filterStatus === 'available' ? isAvailable : !isAvailable
  })

  const filterLabels: Record<FilterStatus, string> = {
    all: `All (${items.length})`,
    available: `Available (${availableCount})`,
    unavailable: `Unavailable (${unavailableCount})`,
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
          <ImageIcon className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No items found</h3>
        <p className="text-sm text-muted-foreground">
          Get started by adding your first {isFoodVendor ? 'food item' : 'clothing product'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Desktop: Segmented Control */}
        <div className="hidden sm:flex items-center">
          <div className="inline-flex items-center rounded-lg border bg-muted/40 p-1">
            {(['all', 'available', 'unavailable'] as FilterStatus[]).map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={cn(
                  'relative px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200',
                  filterStatus === status
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {status === 'all' && 'All'}
                {status === 'available' && (
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    Available
                  </span>
                )}
                {status === 'unavailable' && (
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-orange-500" />
                    Unavailable
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile: Dropdown Filter */}
        <div className="sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  {filterLabels[filterStatus]}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => setFilterStatus('all')}>
                <span className="flex items-center justify-between w-full">
                  All items
                  <Badge variant="secondary" className="ml-2">{items.length}</Badge>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterStatus('available')}>
                <span className="flex items-center justify-between w-full">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    Available
                  </span>
                  <Badge variant="secondary" className="ml-2">{availableCount}</Badge>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterStatus('unavailable')}>
                <span className="flex items-center justify-between w-full">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500" />
                    Unavailable
                  </span>
                  <Badge variant="secondary" className="ml-2">{unavailableCount}</Badge>
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-xl overflow-hidden bg-card shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Item
                </th>
                {!isFoodVendor && (
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                    Brand
                  </th>
                )}
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {isFoodVendor ? 'Price' : 'Price Range'}
                </th>
                {!isFoodVendor && (
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">
                    Variants
                  </th>
                )}
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Status
              </th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Actions
              </th>
            </tr>
          </thead>
            <tbody className="divide-y divide-border bg-card">
              {filteredItems.map((item) => (
                <InventoryTableRow
                  key={item.id}
                  item={item}
                  isFoodVendor={isFoodVendor}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onToggleAvailability={onToggleAvailability}
                  onViewVariants={onViewVariants}
                  loading={loading}
                />
              ))}
            </tbody>
          </table>
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p>No items match your search criteria</p>
            <button
              onClick={() => {
                setSearchQuery('')
                setFilterStatus('all')
              }}
              className="mt-2 text-primary hover:underline text-sm"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          Showing {filteredItems.length} of {items.length} items
        </span>
        {filterStatus !== 'all' && (
          <button
            onClick={() => setFilterStatus('all')}
            className="text-primary hover:underline"
          >
            Show all
          </button>
        )}
                    </div>
                  </div>
  )
}

interface InventoryTableRowProps {
  item: InventoryItem
  isFoodVendor: boolean
  onEdit: (item: InventoryItem) => void
  onDelete: (item: InventoryItem) => void
  onToggleAvailability: (item: FoodItem) => void
  onViewVariants: (item: ClothingItem) => void
  loading: boolean
}

function InventoryTableRow({
  item,
  isFoodVendor,
  onEdit,
  onDelete,
  onToggleAvailability,
  onViewVariants,
  loading,
}: InventoryTableRowProps) {
  if (isFoodVendor) {
    const foodItem = item as FoodItem
    const foodImageSrc = typeof foodItem.imageUrl === 'string' ? foodItem.imageUrl : ''

    return (
      <tr className="hover:bg-muted/30 transition-colors group">
        <td className="px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden flex-shrink-0 border bg-muted">
              <img
                src={foodImageSrc || '/placeholder.svg'}
                alt={foodItem.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-foreground truncate">{foodItem.name}</div>
              <div className="text-sm text-muted-foreground line-clamp-1 mt-0.5">{foodItem.description}</div>
              {foodItem.categories && foodItem.categories.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {[...foodItem.categories]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .slice(0, 2)
                    .map((cat) => (
                      <Badge key={cat.id} variant="secondary" className="text-xs">
                        {cat.name}
                      </Badge>
                    ))}
                  {foodItem.categories.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                      +{foodItem.categories.length - 2}
                      </Badge>
                    )}
                  </div>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 sm:px-6 py-4">
          <span className="text-base font-semibold text-foreground">₹{foodItem.price}</span>
                </td>
        <td className="px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                foodItem.isAvailable
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                  : 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
              )}
            >
              <span
                className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  foodItem.isAvailable ? 'bg-emerald-500' : 'bg-orange-500'
                )}
              />
              {foodItem.isAvailable ? 'Available' : 'Unavailable'}
            </div>
                    <Switch
              checked={foodItem.isAvailable}
              onCheckedChange={() => onToggleAvailability(foodItem)}
                      disabled={loading}
                    />
          </div>
        </td>
        <td className="px-4 sm:px-6 py-4">
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(item)}
              disabled={loading}
              className="h-8 w-8 opacity-60 hover:opacity-100 transition-opacity"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(item)}
              disabled={loading}
              className="h-8 w-8 text-destructive hover:text-destructive opacity-60 hover:opacity-100 transition-opacity"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </td>
      </tr>
    )
  } else {
    const clothingItem = item as ClothingItem
    const minPrice = Math.min(...clothingItem.variants.map((v) => v.price))
    const maxPrice = Math.max(...clothingItem.variants.map((v) => v.price))
    const isAvailable = clothingItem.variants.some((v) => v.stockQty > 0)
    const totalStock = clothingItem.variants.reduce((sum, v) => sum + v.stockQty, 0)

    return (
      <tr className="hover:bg-muted/30 transition-colors group">
        <td className="px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden flex-shrink-0 border bg-muted">
              <img
                src={clothingItem.imageUrl || '/placeholder-image.png'}
                alt={clothingItem.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-foreground truncate">{clothingItem.name}</div>
              <div className="text-sm text-muted-foreground line-clamp-1 mt-0.5 md:hidden">{clothingItem.brand}</div>
              <div className="text-sm text-muted-foreground line-clamp-1 mt-0.5 hidden md:block">{clothingItem.description}</div>
              {clothingItem.categories && clothingItem.categories.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {[...clothingItem.categories]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .slice(0, 2)
                    .map((cat) => (
                      <Badge key={cat.id} variant="secondary" className="text-xs">
                        {cat.name}
                      </Badge>
                    ))}
                  {clothingItem.categories.length > 2 && (
                    <Badge variant="outline" className="text-xs">
                      +{clothingItem.categories.length - 2}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 sm:px-6 py-4 hidden md:table-cell">
          <span className="text-sm font-medium text-foreground">{clothingItem.brand}</span>
        </td>
        <td className="px-4 sm:px-6 py-4">
          <div className="text-sm">
            {minPrice === maxPrice ? (
              <span className="font-semibold text-foreground">₹{minPrice}</span>
            ) : (
              <span className="font-semibold text-foreground">
                ₹{minPrice} - ₹{maxPrice}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 sm:px-6 py-4 hidden lg:table-cell">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-foreground">{clothingItem.variants.length} variants</span>
            <span className="text-xs text-muted-foreground">{totalStock} in stock</span>
          </div>
        </td>
        <td className="px-4 sm:px-6 py-4">
          <div
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
              isAvailable
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                : 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
            )}
          >
            <span
              className={cn('w-1.5 h-1.5 rounded-full', isAvailable ? 'bg-emerald-500' : 'bg-orange-500')}
            />
            {isAvailable ? 'In Stock' : 'Out of Stock'}
          </div>
                </td>
        <td className="px-4 sm:px-6 py-4">
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewVariants(clothingItem)}
              disabled={loading}
              className="h-8 px-2 text-xs opacity-60 hover:opacity-100 transition-opacity"
            >
              <Eye className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">View</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(item)}
              disabled={loading}
              className="h-8 w-8 opacity-60 hover:opacity-100 transition-opacity"
            >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
              size="icon"
                      onClick={() => onDelete(item)}
                      disabled={loading}
              className="h-8 w-8 text-destructive hover:text-destructive opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
  )
  }
}
